// src\components\dashboard\ResponsiveDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Filter, Users, Settings, Home, Monitor } from 'lucide-react';
import { Link } from 'react-router-dom';
import EnhancedProductionCalendar from '../common/EnhancedProductionCalendar';
import BatchDetail from './BatchDetail';
import TVBatchDetail from './TVBatchDetail';
import MobileResourceList from '../common/MobileResourceList';
import ResourceStatusPanel from '../common/ResourceStatusPanel';
import ProductionProgress from '../common/ProductionProgress';
import { useData } from '../../contexts/DataContext';

/**
 * Dashboard responsive qui s'adapte à la taille de l'écran
 */
const ResponsiveDashboard = () => {
  const { 
    pendingOrders, 
    batchAssignments, 
    resources, 
    stats,
    loading, 
    error 
  } = useData();
  
  // Utiliser la date fixe du 31/03/2025 comme point de départ
  const [selectedDate, setSelectedDate] = useState(new Date('2025-03-31'));
  const [visibleBatches, setVisibleBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Détecter la taille de l'écran pour le mode mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Vérifier initialement
    checkMobile();
    
    // Ajouter un écouteur pour les changements de taille
    window.addEventListener('resize', checkMobile);
    
    // Nettoyer l'écouteur
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Générer les créneaux horaires pour le calendrier
  const generateTimeSlots = () => {
    const slots = [];
    const date = new Date(selectedDate);
    
    // Générer les créneaux pour 3 jours à partir de la date sélectionnée
    for (let d = 0; d < 3; d++) {
      const currentDate = new Date(date);
      currentDate.setDate(currentDate.getDate() + d);
      
      const formattedDate = currentDate.toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      });
      
      slots.push(
        { id: `${d}-matin`, name: 'Matin', date: formattedDate },
        { id: `${d}-aprem`, name: 'Après-midi', date: formattedDate },
        { id: `${d}-nuit`, name: 'Nuit', date: formattedDate }
      );
    }
    
    return slots;
  };
  
  // Créneaux horaires pour le calendrier
  const timeSlots = generateTimeSlots();

  // Ajouter timeSlotId aux fournées pour l'affichage
  const batchesWithTimeSlots = batchAssignments.map(batch => {
    // Adapter les heures pour correspondre aux créneaux
    const hour = parseInt(batch.startTime.split(':')[0]);
    let period;
    
    if (hour >= 6 && hour < 14) {
      period = 'matin';
    } else if (hour >= 14 && hour < 22) {
      period = 'aprem';
    } else {
      period = 'nuit';
    }
    
    // Déterminer le jour à partir de la date
    const today = new Date(selectedDate);
    today.setHours(0, 0, 0, 0);
    
    const batchDateParts = batch.date.split('/');
    const batchDate = new Date(`${batchDateParts[2]}-${batchDateParts[1]}-${batchDateParts[0]}`);
    batchDate.setHours(0, 0, 0, 0);
    
    // Calculer la différence en jours
    const diffInDays = Math.floor((batchDate - today) / (1000 * 60 * 60 * 24));
    
    // Vérifier si la date est dans la plage de 3 jours
    if (diffInDays >= 0 && diffInDays <= 2) {
      const timeSlotId = `${diffInDays}-${period}`;
      return { ...batch, timeSlotId };
    } else {
      return null;
    }
  }).filter(batch => batch !== null);
    
  // Fonction pour formater la date pour l'affichage
  const formatDateRange = () => {
    const startDate = new Date(selectedDate);
    const endDate = new Date(selectedDate);
    endDate.setDate(endDate.getDate() + 2);
    
    return `${startDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' })} - ${endDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}`;
  };
  
  // Gérer le clic sur une fournée dans le calendrier
  const handleBatchClick = (batch) => {
    const fullBatch = batchAssignments.find(b => b.batchNumber === batch.batchNumber);
    if (fullBatch) {
      setSelectedBatch(fullBatch);
      
      // Si cette fournée n'est pas déjà visible, l'ajouter aux fournées visibles
      if (!visibleBatches.some(b => b.batchNumber === fullBatch.batchNumber)) {
        // Toujours limiter à 4 fournées visibles maximum
        if (visibleBatches.length >= 4) {
          // Remplacer la plus ancienne (première) par la nouvelle
          setVisibleBatches([...visibleBatches.slice(1), fullBatch]);
        } else {
          // Ajouter à la fin
          setVisibleBatches([...visibleBatches, fullBatch]);
        }
      }
    }
  };
  
  // Trouver les fournées en cours
  const getCurrentBatches = () => {
    // Prioritiser les fournées en cours, puis prendre quelques fournées programmées si nécessaire
    const inProgressBatches = batchAssignments.filter(batch => batch.status === 'in-progress');
    
    // Si on a déjà 4+ fournées en cours, limiter à 4
    if (inProgressBatches.length >= 4) {
      return inProgressBatches.slice(0, 4);
    }
    
    // Sinon, on peut compléter avec des fournées programmées
    return inProgressBatches;
  };
  
  // Fournées en cours pour l'affichage
  const currentBatches = getCurrentBatches();
  
  // Au chargement, afficher les premières fournées disponibles
  useEffect(() => {
    if (batchAssignments.length > 0 && visibleBatches.length === 0) {
      // D'abord afficher les fournées en cours
      let batchesToShow = [...currentBatches];

      // Si pas assez de fournées en cours, ajouter des prochaines fournées programmées
      if (batchesToShow.length < 4) {
        const upcomingBatches = batchAssignments
          .filter(batch => {
            // Exclure les fournées déjà sélectionnées
            return !batchesToShow.some(b => b.batchNumber === batch.batchNumber) &&
                   batch.status === 'scheduled'; // uniquement les programmées
          })
          .sort((a, b) => {
            // Trier par date et heure de début
            const dateA = a.date.split('/').reverse().join('');
            const dateB = b.date.split('/').reverse().join('');
            
            if (dateA !== dateB) return dateA.localeCompare(dateB);
            return a.startTime.localeCompare(b.startTime);
          })
          .slice(0, 4 - batchesToShow.length); // Limite à ce qui reste pour arriver à 4
        
        batchesToShow = [...batchesToShow, ...upcomingBatches];
      }
      
      // Protection supplémentaire: s'assurer qu'on n'a jamais plus de 4 items
      batchesToShow = batchesToShow.slice(0, 4);
      
      setVisibleBatches(batchesToShow);
      
      if (batchesToShow.length > 0) {
        setSelectedBatch(batchesToShow[0]);
      }
    }
  }, [batchAssignments, isMobile]);

  // Si les données sont en cours de chargement
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // Si une erreur s'est produite
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
          <p className="font-bold">Erreur</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  
  // Préparer les statistiques pour l'affichage
  const displayStats = stats || {
    totalScheduled: 0,
    totalCompleted: 0,
    totalInProgress: 0,
    totalAlerts: 0
  };

  // Contenu à afficher pour les appareils mobiles
  if (isMobile) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-100">
        {/* Header */}
        <header className="bg-blue-800 text-white py-2">
          <div className="px-4">
            <div className="flex justify-between items-center">
              <h1 className="text-lg font-bold">PolyPlan</h1>
              <div className="flex space-x-2">
                <Link to="/manager" className="p-2 bg-blue-700 hover:bg-blue-600 rounded-full" title="Vue Manager">
                  <Users size={18} />
                </Link>
                <Link to="/configuration" className="p-2 bg-blue-700 hover:bg-blue-600 rounded-full" title="Configuration">
                  <Settings size={18} />
                </Link>
              </div>
            </div>
            <div className="flex items-center mt-1 text-sm">
              <Calendar size={14} className="mr-1" />
              <span>{formatDateRange()}</span>
            </div>
          </div>
        </header>
        
        {/* Contenu principal */}
        <div className="flex-grow p-2 space-y-4 pb-16">
          {/* Statistiques */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-green-100 p-2 rounded-lg border border-green-200 flex flex-col items-center justify-center">
              <div className="text-sm text-green-800">Terminés</div>
              <div className="text-xl font-bold text-green-800">{displayStats.totalCompleted}</div>
            </div>
            <div className="bg-blue-100 p-2 rounded-lg border border-blue-200 flex flex-col items-center justify-center">
              <div className="text-sm text-blue-800">En cours</div>
              <div className="text-xl font-bold text-blue-800">{displayStats.totalInProgress}</div>
            </div>
            <div className="bg-red-100 p-2 rounded-lg border border-red-200 flex flex-col items-center justify-center">
              <div className="text-sm text-red-800">Alertes</div>
              <div className="text-xl font-bold text-red-800">{displayStats.totalAlerts}</div>
            </div>
          </div>
          
          {/* Liste des ressources */}
          <MobileResourceList 
            resources={resources}
            batches={batchAssignments}
          />
          
          {/* Fournées visibles */}
          <div className="space-y-4">
            <h2 className="font-bold text-gray-700 p-2">Fournées en cours</h2>
            {visibleBatches.map(batch => (
              <TVBatchDetail 
                key={batch.batchNumber}
                batch={batch}
                orders={pendingOrders.filter(order => order.assignedTo === batch.batchNumber)}
                resource={resources.find(r => r.id === batch.resourceId)}
              />
            ))}
          </div>
        </div>
        
        {/* Navigation fixe en bas */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-2 flex justify-around">
          <Link to="/" className="flex flex-col items-center p-2 text-blue-600">
            <Home size={20} />
            <span className="text-xs mt-1">Production</span>
          </Link>
          <Link to="/manager" className="flex flex-col items-center p-2 text-gray-600">
            <Users size={20} />
            <span className="text-xs mt-1">Planning</span>
          </Link>
          <Link to="/configuration" className="flex flex-col items-center p-2 text-gray-600">
            <Settings size={20} />
            <span className="text-xs mt-1">Config</span>
          </Link>
          <Link to="/tv" className="flex flex-col items-center p-2 text-gray-600">
            <Monitor size={20} />
            <span className="text-xs mt-1">TV</span>
          </Link>
        </nav>
      </div>
    );
  }

  // Contenu à afficher pour les ordinateurs de bureau
  return (
    <div className="flex flex-col h-screen bg-gray-100 overflow-hidden">
      {/* Header - inchangé */}
      <header className="bg-blue-800 text-white py-2">
        <div className="flex justify-between items-center px-4">
            <div className="flex items-center">
                {/* Logo ajouté ici */}
                <img src="/logo_bleu.png" alt="Logo" className="h-14 mr-3" />
                <h1 className="text-xl font-bold">Planification des Moyens Chauds</h1>
                <div className="flex items-center ml-4">
                <Calendar className="mr-2" size={16} />
                <span className="font-medium text-sm">{formatDateRange()}</span>
                </div>
            </div>
            <div className="flex items-center space-x-3">
                <div className="bg-green-500 bg-opacity-20 px-2 py-1 rounded-lg border border-green-300 text-white">
                <div className="text-xs font-medium">Complétés</div>
                <div className="text-lg font-bold">{displayStats.totalCompleted}/{displayStats.totalScheduled}</div>
                </div>
                <div className="bg-yellow-500 bg-opacity-20 px-2 py-1 rounded-lg border border-yellow-300 text-white">
                <div className="text-xs font-medium">En cours</div>
                <div className="text-lg font-bold">{displayStats.totalInProgress}/{displayStats.totalScheduled}</div>
                </div>
                <div className="bg-red-500 bg-opacity-20 px-2 py-1 rounded-lg border border-red-300 text-white">
                <div className="text-xs font-medium">Alertes</div>
                <div className="text-lg font-bold">{displayStats.totalAlerts}</div>
                </div>
                
                {/* Liens de navigation */}
                <div className="flex items-center ml-4">
                <Link to="/manager" className="p-2 bg-blue-700 hover:bg-blue-600 rounded-full flex items-center justify-center" title="Vue Manager">
                    <Users size={20} />
                </Link>
                <Link to="/configuration" className="ml-2 p-2 bg-blue-700 hover:bg-blue-600 rounded-full flex items-center justify-center" title="Configuration">
                    <Settings size={20} />
                </Link>
                </div>
            </div>
            </div>
      </header>
      
      {/* Nouvelle structure: Flex row pour le calendrier et les indicateurs */}
      <div className="flex flex-1 overflow-hidden">
        {/* Calendrier à gauche - largeur ajustée */}
        <div className="flex-grow p-3 overflow-auto" style={{ width: "75%" }}>
            <EnhancedProductionCalendar 
                resources={resources}
                timeSlots={timeSlots}
                batchAssignments={batchesWithTimeSlots}
                onBatchClick={handleBatchClick}
                readOnly={true}
                pendingOrders={pendingOrders} // Ajoutez cette ligne
            />
          
          {/* Contrôles pour la navigation dans le calendrier 
          <div className="mt-2 flex justify-start items-center">
            <div className="flex items-center space-x-2">
              <button 
                className="p-1 rounded hover:bg-gray-200"
                onClick={() => {
                  const prevDate = new Date(selectedDate);
                  prevDate.setDate(prevDate.getDate() - 1);
                  setSelectedDate(prevDate);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button 
                className="px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded"
                onClick={() => setSelectedDate(new Date('2025-03-31'))}
              >
                Aujourd'hui
              </button>
              <button 
                className="p-1 rounded hover:bg-gray-200"
                onClick={() => {
                  const nextDate = new Date(selectedDate);
                  nextDate.setDate(nextDate.getDate() + 1);
                  setSelectedDate(nextDate);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>*/}
        </div>
        
        {/* Indicateurs de progression à droite */}
        <div className="p-6 overflow-auto" style={{ width: "25%" }}>
          <ProductionProgress 
            stats={displayStats} 
            currentDate={new Date()} 
            isDetailed={true} 
          />
        </div>
      </div>
      
      {/* Détails des fournées en bas */}
      <div className="p-2 overflow-auto" style={{ height: "52%" }}>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {visibleBatches.map(batch => (
            <BatchDetail 
              key={batch.batchNumber}
              batch={batch}
              orders={pendingOrders.filter(order => order.assignedTo === batch.batchNumber)}
              resource={resources.find(r => r.id === batch.resourceId)}
              isSelected={selectedBatch?.batchNumber === batch.batchNumber}
              onSelect={() => setSelectedBatch(batch)}
            />
          ))}
        </div>
      </div>
      <footer className="bg-gray-100 border-t py-1 px-4 text-center text-xs text-gray-600">
      © 2025 PolyPlan - Application de planification des moyens chauds | Éditeur : KaiDaTa
      </footer>
    </div>
  );
};

export default ResponsiveDashboard;